import React, { useState, useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { apiCall } from "../../services/ApiCall";
import config from "../../config/config.json";

const ReassignModal = ({ show, onHide, requestId, companyId, siteId, onSuccess }) => {
  const [selectedDesignation, setSelectedDesignation] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const designations = [
    { value: 'site_admin', label: 'Site Admin' },
    { value: 'site_manager', label: 'Site Manager' },
    { value: 'main_contact', label: 'Main Contact' }
  ];

  // Fetch all users when modal opens
  useEffect(() => {
    if (show) {
      fetchAllUsers();
    } else {
      // Reset form when modal closes
      setSelectedDesignation('');
      setSelectedUser('');
      setUsersList([]);
    }
  }, [show]);

  const handleDesignationChange = (e) => {
    const designation = e.target.value;
    setSelectedDesignation(designation);
    setSelectedUser('');
  };

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      
      // Call all APIs in parallel
      const [companyResponse, siteResponse, ownerResponse] = await Promise.allSettled([
        fetchCompanyContacts(),
        fetchSiteContacts(),
        fetchOwnerList()
      ]);

      // Combine all users from different sources
      const allUsers = [];

      // Extract company users
      if (companyResponse.status === 'fulfilled' && companyResponse.value) {
        allUsers.push(...companyResponse.value);
      }

      // Extract site users
      if (siteResponse.status === 'fulfilled' && siteResponse.value) {
        allUsers.push(...siteResponse.value);
      }

      // Extract owner users
      if (ownerResponse.status === 'fulfilled' && ownerResponse.value) {
        allUsers.push(...ownerResponse.value);
      }

      // Remove duplicates based on user ID
      const uniqueUsers = removeDuplicateUsers(allUsers);
      
      setUsersList(uniqueUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsersList([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchCompanyContacts = async () => {
    if (!companyId) {
      console.log("Company ID not provided, skipping company contacts");
      return [];
    }

    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getCompanyContact`,
        {},
        { companyId: companyId },
        "GET"
      );
      
      if (isSuccess && data?.data?.comapnyUserData) {
        return data.data.comapnyUserData.map(user => ({
          ...user,
          source: 'Company'
        }));
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch company contacts:", error);
      return [];
    }
  };

  const fetchSiteContacts = async () => {
    if (!siteId) {
      console.log("Site ID not provided, skipping site contacts");
      return [];
    }

    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getSitesContact`,
        {},
        { siteId: siteId },
        "GET"
      );
      
      if (isSuccess && data?.data?.sitesUserData) {
        return data.data.sitesUserData.map(user => ({
          ...user,
          source: 'Site'
        }));
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch site contacts:", error);
      return [];
    }
  };

  const fetchOwnerList = async () => {
    if (!requestId) {
      console.log("Request ID not provided, skipping owner list");
      return [];
    }

    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getOwnerListList`,
        {},
        { requestId: requestId },
        "GET"
      );
      
      if (isSuccess && data?.data) {
        // Adjust based on actual response structure
        const ownerUsers = Array.isArray(data.data) ? data.data :
                          data.data.ownerList ? data.data.ownerList :
                          data.data.comapnyUserData ? data.data.comapnyUserData : [];
        
        return ownerUsers.map(user => ({
          ...user,
          source: 'Owner'
        }));
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch owner list:", error);
      return [];
    }
  };

  const removeDuplicateUsers = (users) => {
    const uniqueUsersMap = new Map();
    
    users.forEach(user => {
      const userId = user.id;
      if (userId) {
        // If user already exists, keep the one with more complete data or combine sources
        if (uniqueUsersMap.has(userId)) {
          const existingUser = uniqueUsersMap.get(userId);
          // Combine sources if it's the same user from different places
          if (existingUser.source !== user.source) {
            existingUser.source = `${existingUser.source}, ${user.source}`;
          }
        } else {
          uniqueUsersMap.set(userId, user);
        }
      }
    });
    
    return Array.from(uniqueUsersMap.values());
  };

  const getFilteredUsers = () => {
    if (!selectedDesignation) {
      return [];
    }

    return usersList.filter(user => {
      const userDesignation = user.designation?.toLowerCase().trim() || '';
      
      switch (selectedDesignation) {
        case 'site_admin':
          // Site Admin can see: Site Admin and Owner
          return userDesignation === 'site admin' || userDesignation === 'owner';
        
        case 'site_manager':
          // Site Manager can see: Site Manager and Owner
          return userDesignation === 'site manager' || userDesignation === 'owner';
        
        case 'main_contact':
          // Main Contact can see: Owner only
          return userDesignation === 'owner';
        
        default:
          return false;
      }
    });
  };

  const handleSubmit = async () => {
    if (!selectedDesignation || !selectedUser) {
      alert('Please select both designation and user');
      return;
    }

    try {
      setSubmitting(true);
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}reassign-request`,
        {},
        {
          requestID: parseInt(requestId),
          roleType: selectedDesignation,
          assignedTo: parseInt(selectedUser)
        },
        "POST"
      );
      
      if (isSuccess) {
        alert('Request reassigned successfully');
        onHide();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        alert('Failed to reassign request. Please try again.');
      }
    } catch (error) {
      console.error("Failed to reassign request:", error);
      alert('Failed to reassign request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onHide();
    }
  };

  const filteredUsers = getFilteredUsers();

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
    >
      <Modal.Header style={styles.header}>
        <Modal.Title style={styles.title}>Reassign Request</Modal.Title>
        <button 
          onClick={handleClose} 
          style={styles.closeButton}
          aria-label="Close"
          disabled={submitting}
        >
          ×
        </button>
      </Modal.Header>
      
      <Modal.Body style={styles.body}>
        {loadingUsers ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loader}></div>
            <p style={styles.loadingText}>Loading users...</p>
          </div>
        ) : (
          <Form>
            <Form.Group className="mb-4">
              <Form.Label style={styles.label}>
                Select Designation <span style={styles.required}>*</span>
              </Form.Label>
              <Form.Select
                value={selectedDesignation}
                onChange={handleDesignationChange}
                style={styles.select}
                disabled={submitting || loadingUsers}
              >
                <option value="">Choose designation...</option>
                {designations.map((designation) => (
                  <option key={designation.value} value={designation.value}>
                    {designation.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={styles.label}>
                Select User <span style={styles.required}>*</span>
              </Form.Label>
              <Form.Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                disabled={!selectedDesignation || submitting || loadingUsers}
                style={styles.select}
              >
                <option value="">Choose user...</option>
                {filteredUsers.map((user) => (
                  <option 
                    key={`${user.id}-${user.source}`} 
                    value={user.id}
                    style={styles.userOption}
                  >
                    {user.name}
                    {user.designation ? ` • ${user.designation}` : ''}
                    {user.email ? ` • ${user.email}` : ''}
                  </option>
                ))}
              </Form.Select>
              {selectedDesignation && filteredUsers.length === 0 && !loadingUsers && (
                <small style={styles.helpText}>No users found for this designation</small>
              )}
              {selectedDesignation && filteredUsers.length > 0 && (
                <small style={styles.infoText}>
                  {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} available
                </small>
              )}
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      
      <Modal.Footer style={styles.footer}>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          style={styles.cancelButton}
          disabled={submitting || loadingUsers}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={!selectedDesignation || !selectedUser || submitting || loadingUsers}
          style={{
            ...styles.submitButton,
            opacity: (!selectedDesignation || !selectedUser || submitting || loadingUsers) ? 0.6 : 1
          }}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const styles = {
  header: {
    backgroundColor: '#0050a4',
    color: '#ffffff',
    borderBottom: 'none',
    padding: '20px 24px',
    position: 'relative',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    margin: 0,
    color: '#ffffff',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#ffffff',
    fontSize: '32px',
    lineHeight: '1',
    cursor: 'pointer',
    padding: 0,
    position: 'absolute',
    right: '20px',
    top: '16px',
    opacity: 0.8,
    transition: 'opacity 0.2s',
  },
  body: {
    padding: '32px 24px',
    backgroundColor: '#ffffff',
    minHeight: '200px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  loader: {
    border: '4px solid #dcedff',
    borderTop: '4px solid #0050a4',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: '#0050a4',
    fontSize: '14px',
    fontWeight: '500',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#0050a4',
    marginBottom: '8px',
    display: 'block',
  },
  required: {
    color: '#dc3545',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    color: '#333333',
    transition: 'all 0.2s',
    outline: 'none',
  },
  userOption: {
    padding: '8px 12px',
  },
  helpText: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '6px',
    display: 'block',
  },
  infoText: {
    color: '#0050a4',
    fontSize: '12px',
    marginTop: '6px',
    display: 'block',
    fontWeight: '500',
  },
  footer: {
    backgroundColor: '#dcedff',
    borderTop: 'none',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  cancelButton: {
    backgroundColor: '#ffffff',
    color: '#0050a4',
    border: '1px solid #0050a4',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '500',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButton: {
    backgroundColor: '#0050a4',
    color: '#ffffff',
    border: 'none',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '500',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

// Add CSS animation for loader
const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default ReassignModal;